import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * 统一折扣码的比较口径（与 ug-auto-discount.js 的 normalizeCode 一致）：
 * 手机粘贴常带首尾空格，服务端返回的大小写也不一定跟用户输入相同。
 * @param {string | null | undefined} code
 * @returns {string}
 */
function normalizeCode(code) {
  return String(code || '')
    .trim()
    .toLowerCase();
}

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /**
   * 串行执行折扣码写入，不用 abort 抢。
   * cart/update 的 discount 是全量覆盖，abort 只放弃读响应、POST 早就在服务端生效了；
   * 而 #existingDiscounts() 把 DOM pill 当唯一真源，被 abort 那次的成功写入没反映到 DOM，
   * 后一次请求就会带着脏列表把它覆盖掉（先提交码 A、1 秒内点 pill B 的 ×，A 会在服务端被删掉）。
   * @type {Promise<void>}
   */
  #updateQueue = Promise.resolve();

  /**
   * @param {() => Promise<void>} job
   * @returns {Promise<void>}
   */
  #enqueue(job) {
    this.#updateQueue = this.#updateQueue.then(job).catch((error) => {
      console.error('[cart-discount] 折扣码队列异常', error);
    });
    return this.#updateQueue;
  }

  /**
   * morphSection 是 async 且会 throw，同步调用会变成 unhandled rejection，
   * 用户侧表现为「点了什么都没发生」。单独兜住，也避免 morph 失败被当成折扣码错误。
   * @param {string} sectionId
   * @param {string} html
   * @returns {Promise<void>}
   */
  async #morphSection(sectionId, html) {
    try {
      await morphSection(sectionId, html);
    } catch (error) {
      console.error('[cart-discount] 折扣区 section 更新失败', error);
    }
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    const sectionId = this.dataset.sectionId;
    if (!(discountCode instanceof HTMLInputElement) || typeof sectionId !== 'string') return;

    // 纯空格能通过 required 校验，但 POST 后找不到对应条目：
    // 既不进错误分支也不进 shipping 分支，只白 morph 一次，用户零反馈
    const discountCodeValue = discountCode.value.trim();
    discountCode.value = discountCodeValue;
    if (!discountCodeValue) return;

    await this.#enqueue(() => this.#applyDiscountNow(sectionId, discountCode, discountCodeValue, event));
  };

  /**
   * @param {string} sectionId
   * @param {HTMLInputElement} discountCode - 折扣码输入框
   * @param {string} discountCodeValue - 已 trim 的折扣码
   * @param {SubmitEvent} event
   * @returns {Promise<void>}
   */
  async #applyDiscountNow(sectionId, discountCode, discountCodeValue, event) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const normalized = normalizeCode(discountCodeValue);

    try {
      // 排在前面的任务已经 morph 完，这里读到的才是最新的 pill 列表
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some((code) => normalizeCode(code) === normalized)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);
      if (!response.ok) throw new Error(`cart/update ${response.status}`);

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return normalizeCode(discount.code) === normalized && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${sectionId}`);
      if (section) {
        /** @type {string[]} */
        const codes = [];
        for (const element of section.querySelectorAll('.cart-discount__pill')) {
          if (element instanceof HTMLLIElement && element.dataset.discountCode) {
            codes.push(element.dataset.discountCode);
          }
        }

        const existingKeys = existingDiscounts.map(normalizeCode);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((code) => existingKeys.includes(normalizeCode(code))) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return normalizeCode(discount.code) === normalized && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await this.#morphSection(sectionId, newHtml);
    } catch (error) {
      console.error('[cart-discount] 应用折扣码失败', error);
      // 整个 catch 空着的话，网络失败时按钮点了毫无反应，用户只会反复点
      this.#handleDiscountError('discount_code');
    } finally {
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  }

  /**
   * 从购物车去掉该折扣码
   * 主题会把点击包成 Proxy，不能用 instanceof MouseEvent 判断
   * @param {Event} event
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event instanceof KeyboardEvent && event.key !== 'Enter') return;

    const source = event.target;
    const sectionId = this.dataset.sectionId;
    if (!(source instanceof Element) || typeof sectionId !== 'string') return;

    const pill = source.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    // pill 可能在排队期间被 morph 换掉，先把码字符串取出来
    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    await this.#enqueue(() => this.#removeDiscountNow(sectionId, discountCode));
  };

  /**
   * @param {string} sectionId
   * @param {string} discountCode
   * @returns {Promise<void>}
   */
  async #removeDiscountNow(sectionId, discountCode) {
    // 排在前面的任务已经 morph 完，这里读到的才是最新的 pill 列表
    const existingDiscounts = this.#existingDiscounts();
    const normalized = normalizeCode(discountCode);
    const index = existingDiscounts.findIndex((code) => normalizeCode(code) === normalized);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);
      if (!response.ok) throw new Error(`cart/update ${response.status}`);

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await this.#morphSection(sectionId, data.sections[sectionId]);
    } catch (error) {
      console.error('[cart-discount] 移除折扣码失败', error);
    }
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
